<template>
  <div class="rewards-page">
    <div class="page-header">
      <h1>Вознаграждения Luxottica</h1>
      <div class="header-actions">
        <div class="rewards-badge" v-if="summaryLoaded">
          <span class="label">Итого к выплате:</span>
          <span class="value">{{ formatCurrency(totalUnpaidRewards) }}</span>
        </div>
        <Button 
          label="Экспорт в Excel" 
          icon="pi pi-file-excel" 
          @click="exportToExcel" 
          :loading="exportLoading"
          class="p-button-success p-button-outlined"
        />
        <Button 
          label="Обновить данные" 
          icon="pi pi-refresh" 
          @click="loadRewardsData" 
          :loading="loading" 
        />
      </div>
    </div>

    <!-- Фильтры -->
    <div class="filters-section">
      <div class="p-field">
        <label for="customerFilter">Клиент</label>
        <InputText 
          id="customerFilter" 
          v-model="filters.customer" 
          placeholder="Поиск по клиенту" 
          @input="debouncedFilter"
        />
      </div>
      <div class="p-field">
        <label for="statusFilter">Статус</label>
        <Select 
          id="statusFilter" 
          v-model="filters.status" 
          :options="statusOptions" 
          optionLabel="label" 
          optionValue="value"
          placeholder="Все статусы"
          @change="applyFilters"
        />
      </div>
      <div class="p-field">
        <label for="dateFromFilter">Дата с</label>
        <DatePicker 
          id="dateFromFilter" 
          v-model="filters.dateFrom" 
          placeholder="Дата начала"
          dateFormat="dd.mm.yy"
          @date-select="applyFilters"
        />
      </div>
      <div class="p-field">
        <label for="dateToFilter">Дата по</label>
        <DatePicker 
          id="dateToFilter" 
          v-model="filters.dateTo" 
          placeholder="Дата окончания"
          dateFormat="dd.mm.yy"
          @date-select="applyFilters"
        />
      </div>
      <div class="p-field filter-actions">
        <Button 
          label="Очистить" 
          icon="pi pi-filter-slash" 
          @click="clearFilters" 
          class="p-button-outlined p-button-secondary"
        />
        <Button
          label="Накладные"
          icon="pi pi-list"
          class="p-button-outlined p-button-info"
          @click="openInvoicesModal"
        />
      </div>
    </div>

    <!-- Основная таблица -->
    <div class="table-section">
      <DataTable 
        :value="filteredRewards" 
        :loading="loading"
        :paginator="true"
        :rows="20"
        :rowsPerPageOptions="[10, 20, 50]"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Показано {first}-{last} из {totalRecords} записей"
        sortMode="multiple"
        removableSort
        v-model:selection="selectedOrders"
        dataKey="order_id"
        :rowClass="getRowClass"
        class="p-datatable-sm"
      >
        <template #header>
          <div class="table-header">
            <span>Заказы Luxottica с вознаграждениями</span>
            <div class="header-buttons" v-if="selectedOrders.length > 0">
              <Button 
                label="Отметить выплаченными" 
                icon="pi pi-check" 
                @click="markAsPaid" 
                class="p-button-success p-button-sm"
              />
              <Button 
                label="Отметить невыплаченными" 
                icon="pi pi-times" 
                @click="markAsUnpaid" 
                class="p-button-danger p-button-sm"
              />
            </div>
          </div>
        </template>

        <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
        
        <Column field="order_id" header="Заказ" sortable>
          <template #body="slotProps">
            <Button 
              :label="slotProps.data.order_id" 
              class="p-button-link" 
              @click="showOrderDetails(slotProps.data.order_id)"
            />
          </template>
        </Column>

        <Column field="order_date" header="Дата заказа" sortable>
          <template #body="slotProps">
            {{ formatDate(slotProps.data.order_date) }}
          </template>
        </Column>

        <Column field="customer_name" header="Клиент" sortable>
          <template #body="slotProps">
            <div class="customer-cell">
              <span class="customer-name">{{ slotProps.data.customer_name }}</span>
            </div>
          </template>
        </Column>

        <Column field="total_amount" header="Сумма заказа" sortable>
          <template #body="slotProps">
            {{ formatCurrency(slotProps.data.total_amount) }}
          </template>
        </Column>

        <Column field="reward_amount" header="Вознаграждение" sortable>
          <template #body="slotProps">
            <span class="reward-amount">{{ formatCurrency(slotProps.data.reward_amount) }}</span>
          </template>
        </Column>

        <Column field="paid_items" header="Позиции" sortable>
          <template #body="slotProps">
            <Tag 
              :value="slotProps.data.paid_items" 
              severity="info" 
            />
          </template>
        </Column>

        <Column field="status" header="Статус" sortable>
          <template #body="slotProps">
            <Tag 
              :value="slotProps.data.status" 
              :severity="getStatusSeverity(slotProps.data.status)"
            />
          </template>
        </Column>

        <Column field="unpaid_rewards" header="К выплате" sortable>
          <template #body="slotProps">
            <Tag 
              :value="slotProps.data.unpaid_rewards > 0 ? 'Да' : 'Нет'" 
              :severity="slotProps.data.unpaid_rewards > 0 ? 'danger' : 'success'"
            />
          </template>
        </Column>

        <Column header="Действия" :exportable="false">
          <template #body="slotProps">
            <div class="action-buttons">
              <Button 
                icon="pi pi-eye" 
                class="p-button-rounded p-button-outlined p-button-sm" 
                @click="showOrderDetails(slotProps.data.order_id)"
                v-tooltip="'Детали заказа'"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Диалог деталей заказа -->
    <Dialog 
      v-model:visible="orderDetailsVisible" 
      :header="'Детали заказа ' + selectedOrderId"
      :style="{ width: '80vw' }"
      maximizable
      modal
    >
      <div v-if="orderDetailsLoading" class="loading-container">
        <ProgressSpinner />
        <p>Загрузка деталей заказа...</p>
      </div>
      
      <div v-else-if="orderDetails" class="order-details">
        <!-- Информация о заказе -->
        <div class="order-info">
          <h3>Информация о заказе</h3>
          <div class="info-grid">
            <div class="info-item">
              <label>Заказ:</label>
              <span>{{ orderDetails.order_id }}</span>
            </div>
            <div class="info-item">
              <label>Дата:</label>
              <span>{{ formatDate(orderDetails.order_date) }}</span>
            </div>
            <div class="info-item">
              <label>Клиент:</label>
              <span>{{ orderDetails.customer_name }}</span>
            </div>
            <div class="info-item">
              <label>Итого вознаграждение:</label>
              <span class="reward-total">{{ formatCurrency(orderDetails.total_reward) }}</span>
            </div>
          </div>
        </div>

        <!-- Таблица позиций -->
        <div class="items-section">
          <div class="items-header">
            <h3>Позиции с вознаграждениями</h3>
            <div class="items-actions" v-if="selectedItems.length > 0">
              <Button 
                label="Отметить выплаченными" 
                icon="pi pi-check" 
                @click="markSelectedItemsAsPaid" 
                class="p-button-success p-button-sm"
                size="small"
              />
              <Button 
                label="Отметить невыплаченными" 
                icon="pi pi-times" 
                @click="markSelectedItemsAsUnpaid" 
                class="p-button-danger p-button-sm"
                size="small"
              />
            </div>
            <Button 
              label="Экспорт деталей" 
              icon="pi pi-file-excel" 
              @click="exportOrderDetails" 
              class="p-button-outlined p-button-sm"
              size="small"
            />
          </div>
          
          <DataTable 
            :value="orderDetails.items" 
            class="p-datatable-sm"
            responsiveLayout="scroll"
            v-model:selection="selectedItems"
            dataKey="item_id"
          >
            <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
            <Column field="item_id" header="ID" style="width: 80px"></Column>
            <Column field="sku" header="Артикул"></Column>
            <Column field="name" header="Название"></Column>
            <Column field="quantity" header="Кол-во" style="width: 80px">
              <template #body="slotProps">
                <Tag :value="slotProps.data.quantity" />
              </template>
            </Column>
            <Column field="purchase_price" header="Себестоимость">
              <template #body="slotProps">
                {{ formatCurrency(slotProps.data.purchase_price) }}
              </template>
            </Column>
            <Column field="sale_price" header="Цена продажи">
              <template #body="slotProps">
                {{ formatCurrency(slotProps.data.sale_price) }}
              </template>
            </Column>
            <Column field="margin_percent" header="Маржа %" style="width: 80px">
              <template #body="slotProps">
                <Tag 
                  :value="slotProps.data.margin_percent + '%'" 
                  :severity="slotProps.data.margin_percent >= 50 ? 'success' : 'warning'"
                />
              </template>
            </Column>
            <Column field="reward_percent" header="% возн." style="width: 80px">
              <template #body="slotProps">
                <Tag 
                  :value="slotProps.data.reward_percent + '%'" 
                  severity="info"
                />
              </template>
            </Column>
            <Column field="reward_amount" header="Вознаграждение">
              <template #body="slotProps">
                <span class="reward-amount">{{ formatCurrency(slotProps.data.reward_amount) }}</span>
              </template>
            </Column>
            <Column field="payment_status" header="Статус выплаты">
              <template #body="slotProps">
                <div class="status-cell">
                  <Tag 
                    :value="slotProps.data.payment_status" 
                    :severity="slotProps.data.payment_status === 'Выплачено' ? 'success' : 'danger'"
                  />
                  <Button 
                    :icon="slotProps.data.payment_status === 'Выплачено' ? 'pi pi-times' : 'pi pi-check'"
                    :class="slotProps.data.payment_status === 'Выплачено' ? 'p-button-danger' : 'p-button-success'"
                    class="p-button-rounded p-button-outlined p-button-sm status-toggle-btn"
                    @click="toggleItemPaymentStatus(orderDetails.order_id, slotProps.data.item_id, slotProps.data.payment_status)"
                    :title="slotProps.data.payment_status === 'Выплачено' ? 'Отметить невыплаченным' : 'Отметить выплаченным'"
                    size="small"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

      <template #footer>
        <Button 
          label="Закрыть" 
          icon="pi pi-times" 
          @click="orderDetailsVisible = false" 
          class="p-button-text"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="invoicesDialogVisible"
      header="Накладные PartsSoft"
      :style="{ width: '90vw', maxWidth: '1200px' }"
      modal
    >
      <div v-if="invoicesLoading" class="loading-container">
        <ProgressSpinner />
        <p>Загрузка списка накладных...</p>
      </div>

      <div v-else class="invoices-dialog">
        <div class="invoices-table-section">
          <DataTable
            :value="invoices"
            :loading="invoicesLoading"
            :paginator="true"
            :rows="invoicePagination.perPage"
            :totalRecords="invoiceTotalRecords"
            :lazy="true"
            dataKey="id"
            v-model:selection="selectedInvoice"
            selectionMode="single"
            :first="invoiceFirst"
            class="p-datatable-sm invoices-table"
            @page="onInvoicesPage"
            @row-click="event => loadInvoiceDetail(event.data.id)"
          >
            <template #header>
              <div class="invoices-table-header">
                <span>Список накладных</span>
              </div>
            </template>

            <Column field="number" header="Номер">
              <template #body="slotProps">
                <Button
                  class="p-button-link"
                  :label="slotProps.data.number || slotProps.data.id"
                  @click.stop="loadInvoiceDetail(slotProps.data.id)"
                />
              </template>
            </Column>
            <Column field="created_at" header="Дата">
              <template #body="slotProps">
                {{ slotProps.data.created_at ? formatDate(slotProps.data.created_at) : '—' }}
              </template>
            </Column>
            <Column field="total_amount" header="Сумма">
              <template #body="slotProps">
                {{ formatCurrency(slotProps.data.total_amount) }}
              </template>
            </Column>
          </DataTable>
        </div>

        <div class="invoice-detail-section">
          <div v-if="invoiceDetailLoading" class="loading-container">
            <ProgressSpinner />
            <p>Загрузка деталей накладной...</p>
          </div>

          <div v-else-if="invoiceDetail" class="invoice-detail">
            <h3>Информация о накладной</h3>
            <div class="info-grid">
              <div class="info-item">
                <label>Номер</label>
                <span>{{ invoiceDetail.number || invoiceDetail.id }}</span>
              </div>
              <div class="info-item">
                <label>Дата</label>
                <span>{{ formatDate(invoiceDetail.created_at) }}</span>
              </div>
              <div class="info-item">
                <label>Клиент</label>
                <span>{{ invoiceDetail.customer_name || '—' }}</span>
              </div>
              <div class="info-item">
                <label>Сумма</label>
                <span class="reward-amount">{{ formatCurrency(invoiceDetail.total_amount) }}</span>
              </div>
            </div>

            <h4>Позиции накладной</h4>
            <DataTable
              :value="invoiceDetail.items"
              class="p-datatable-sm invoice-items-table"
              responsiveLayout="scroll"
            >
              <Column field="oem" header="OEM" />
              <Column field="make_name" header="Производитель" />
              <Column field="detail_name" header="Наименование" />
              <Column field="quantity" header="Кол-во">
                <template #body="slotProps">
                  <Tag :value="slotProps.data.quantity" severity="info" />
                </template>
              </Column>
              <Column field="price" header="Цена">
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.price) }}
                </template>
              </Column>
              <Column field="sum" header="Сумма">
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.sum) }}
                </template>
              </Column>
              
            </DataTable>
          </div>

          <div v-else class="invoice-detail-placeholder">
            <p>Выберите накладную, чтобы увидеть подробности.</p>
          </div>
        </div>
      </div>

      <template #footer>
        <Button 
          label="Закрыть" 
          icon="pi pi-times" 
          @click="invoicesDialogVisible = false" 
          class="p-button-text"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import apiService from '@/services/apiService'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import DatePicker from 'primevue/datepicker'
import Dialog from 'primevue/dialog'
import Tag from 'primevue/tag'
import ProgressSpinner from 'primevue/progressspinner'
import { useToast } from 'primevue/usetoast'
import { debounce } from 'lodash'

export default {
  name: 'RewardsPage',
  components: {
    DataTable,
    Column,
    Button,
    InputText,
    Select,
    DatePicker,
    Dialog,
    Tag,
    ProgressSpinner
  },
  setup() {
    const toast = useToast()
    
    // Реактивные данные
    const rewardsData = ref([])
    const filteredRewards = ref([])
    const loading = ref(false)
    const exportLoading = ref(false)
    const summaryLoaded = ref(false)
    const selectedOrders = ref([])

    // Накладные PartsSoft
    const invoicesDialogVisible = ref(false)
    const invoicesLoading = ref(false)
    const invoices = ref([])
    const invoicePagination = reactive({
      page: 1,
      perPage: 10,
      hasMore: false,
      total: null
    })
    const invoiceFirst = ref(0)
    const selectedInvoice = ref(null)
    const selectedInvoiceId = ref(null)
    const invoiceDetail = ref(null)
    const invoiceDetailLoading = ref(false)
    
    // Диалог деталей заказа
    const orderDetailsVisible = ref(false)
    const orderDetailsLoading = ref(false)
    const orderDetails = ref(null)
    const selectedOrderId = ref('')
    const selectedItems = ref([])  // Выбранные позиции в диалоге деталей
    
    // Фильтры
    const filters = reactive({
      customer: '',
      status: '',
      dateFrom: null,
      dateTo: null
    })

    // Опции для фильтров
    const statusOptions = ref([
      { label: 'Все статусы', value: '' },
      { label: 'Открыт', value: 'Открыт' },
      { label: 'Закрыт', value: 'Закрыт' }
    ])

    // Вычисляемые свойства
    const totalUnpaidRewards = computed(() => {
      return rewardsData.value.reduce((sum, order) => {
        return sum + (order.unpaid_rewards > 0 ? order.reward_amount : 0)
      }, 0)
    })

    const invoiceTotalRecords = computed(() => {
      if (invoicePagination.total !== null && invoicePagination.total !== undefined) {
        return invoicePagination.total
      }
      const currentCount = invoices.value.length || 0
      const base = (invoicePagination.page - 1) * invoicePagination.perPage
      return base + currentCount + (invoicePagination.hasMore ? invoicePagination.perPage : 0)
    })


    // Методы загрузки данных
    const loadRewardsData = async () => {
      try {
        loading.value = true
        const response = await apiService.get('/rewards/')
        rewardsData.value = response.data
        applyFilters()
        summaryLoaded.value = true
        
        toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: `Загружено ${response.data.length} заказов`,
          life: 3000
        })
      } catch (error) {
        console.error('Ошибка загрузки данных вознаграждений:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить данные',
          life: 5000
        })
      } finally {
        loading.value = false
      }
    }

    // Применение фильтров
    const applyFilters = () => {
      let filtered = [...rewardsData.value]

      // Фильтр по клиенту
      if (filters.customer.trim()) {
        const searchTerm = filters.customer.toLowerCase().trim()
        filtered = filtered.filter(order => 
          order.customer_name.toLowerCase().includes(searchTerm) ||
          order.order_id.toLowerCase().includes(searchTerm)
        )
      }

      // Фильтр по статусу
      if (filters.status) {
        filtered = filtered.filter(order => order.status === filters.status)
      }

      // Фильтр по дате
      if (filters.dateFrom) {
        filtered = filtered.filter(order => 
          new Date(order.order_date) >= filters.dateFrom
        )
      }

      if (filters.dateTo) {
        filtered = filtered.filter(order => 
          new Date(order.order_date) <= filters.dateTo
        )
      }

      filteredRewards.value = filtered
    }

    // Debounced фильтрация для поля поиска
    const debouncedFilter = debounce(applyFilters, 300)

    // Очистка фильтров
    const clearFilters = () => {
      filters.customer = ''
      filters.status = ''
      filters.dateFrom = null
      filters.dateTo = null
      applyFilters()
    }

    const openInvoicesModal = async () => {
      invoicesDialogVisible.value = true
      selectedInvoice.value = null
      selectedInvoiceId.value = null
      invoiceDetail.value = null
      await loadInvoices(1)
    }

    const loadInvoices = async (page = 1) => {
      try {
        invoicesLoading.value = true
        const response = await apiService.getRewardInvoices({
          page,
          per_page: invoicePagination.perPage
        })
        const data = response.data || {}
        invoices.value = data.items || []

        const pagination = data.pagination || {}
        const resolvedPage = Number(pagination.page || page)
        const resolvedPerPage = Number(pagination.per_page || invoicePagination.perPage)
        invoicePagination.page = Number.isNaN(resolvedPage) ? page : resolvedPage
        invoicePagination.perPage = Number.isNaN(resolvedPerPage) ? invoicePagination.perPage : resolvedPerPage
        invoicePagination.hasMore = !!pagination.has_more
        const resolvedTotal = pagination.total ?? null
        if (resolvedTotal === null || resolvedTotal === undefined) {
          invoicePagination.total = null
        } else {
          const totalNumber = Number(resolvedTotal)
          invoicePagination.total = Number.isNaN(totalNumber) ? null : totalNumber
        }
        invoiceFirst.value = (invoicePagination.page - 1) * invoicePagination.perPage

        if (selectedInvoiceId.value) {
          const current = invoices.value.find(item => item.id === selectedInvoiceId.value)
          if (current) {
            selectedInvoice.value = current
            await loadInvoiceDetail(selectedInvoiceId.value, { silent: true })
          } else {
            selectedInvoice.value = null
            selectedInvoiceId.value = null
            invoiceDetail.value = null
          }
        } else if (invoices.value.length) {
          selectedInvoice.value = invoices.value[0]
          selectedInvoiceId.value = invoices.value[0].id
          await loadInvoiceDetail(selectedInvoiceId.value, { silent: true })
        }
      } catch (error) {
        console.error('Ошибка загрузки накладных:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить накладные',
          life: 5000
        })
      } finally {
        invoicesLoading.value = false
      }
    }

    const onInvoicesPage = async (event) => {
      invoicePagination.perPage = event.rows
      invoiceFirst.value = event.first
      await loadInvoices(event.page + 1)
    }

    const loadInvoiceDetail = async (invoiceId, options = {}) => {
      if (!invoiceId) return
      selectedInvoiceId.value = invoiceId
      const found = invoices.value.find(item => item.id === invoiceId)
      if (found) {
        selectedInvoice.value = found
      }
      invoiceDetail.value = null
      invoiceDetailLoading.value = true
      try {
        const response = await apiService.getRewardInvoiceDetail(invoiceId)
        invoiceDetail.value = response.data
      } catch (error) {
        console.error('Ошибка загрузки накладной:', error)
        if (!options.silent) {
          toast.add({
            severity: 'error',
            summary: 'Ошибка',
            detail: 'Не удалось загрузить детали накладной',
            life: 5000
          })
        }
      } finally {
        invoiceDetailLoading.value = false
      }
    }

    // Показ деталей заказа
    const showOrderDetails = async (orderId) => {
      try {
        selectedOrderId.value = orderId
        orderDetailsVisible.value = true
        orderDetailsLoading.value = true
        selectedItems.value = []  // Очищаем выбранные позиции
        
        const response = await apiService.get(`/rewards/${orderId}`)
        orderDetails.value = response.data
      } catch (error) {
        console.error('Ошибка загрузки деталей заказа:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить детали заказа',
          life: 5000
        })
      } finally {
        orderDetailsLoading.value = false
      }
    }

    // Изменение статуса выплаты для отдельной позиции
    const toggleItemPaymentStatus = async (orderId, itemId, currentStatus) => {
      try {
        const newStatus = currentStatus === 'Выплачено' ? 'Не выплачено' : 'Выплачено'
        
        const response = await apiService.put(`/rewards/${orderId}/items/${itemId}/payment-status`, {
          status: newStatus
        })
        
        if (response.data.success) {
          // Обновляем данные в диалоге деталей
          if (orderDetails.value && orderDetails.value.items) {
            const item = orderDetails.value.items.find(i => i.item_id === itemId)
            if (item) {
              item.payment_status = newStatus
            }
          }
          
          // Перезагружаем основной список
          await loadRewardsData()
          
          toast.add({
            severity: 'success',
            summary: 'Успешно',
            detail: `Статус позиции изменен на "${newStatus}"`,
            life: 3000
          })
        }
      } catch (error) {
        console.error('Ошибка изменения статуса позиции:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось изменить статус позиции',
          life: 5000
        })
      }
    }

    // Изменение статуса выплаты для заказа (устаревший метод, оставлен для совместимости)
    const togglePaymentStatus = async (orderId) => {
      toast.add({
        severity: 'info',
        summary: 'Информация',
        detail: 'Используйте изменение статусов отдельных позиций в деталях заказа',
        life: 3000
      })
    }

    // Массовые операции для отдельных позиций
    const markSelectedItemsAsPaid = async () => {
      try {
        if (!selectedItems.value.length) {
          toast.add({
            severity: 'warn',
            summary: 'Предупреждение',
            detail: 'Выберите позиции для изменения статуса',
            life: 3000
          })
          return
        }

        const orderItemPairs = selectedItems.value.map(item => [
          orderDetails.value.order_id,
          item.item_id
        ])

        const response = await apiService.put('/rewards/batch-update-status', {
          order_item_pairs: orderItemPairs,
          status: 'Выплачено'
        })

        if (response.data.success) {
          // Обновляем статусы в диалоге
          selectedItems.value.forEach(item => {
            item.payment_status = 'Выплачено'
          })
          
          selectedItems.value = []
          await loadRewardsData()
          
          toast.add({
            severity: 'success',
            summary: 'Успешно',
            detail: `Обновлено ${response.data.updated_count} позиций`,
            life: 3000
          })
        }
      } catch (error) {
        console.error('Ошибка массового обновления:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось обновить статусы позиций',
          life: 5000
        })
      }
    }

    const markSelectedItemsAsUnpaid = async () => {
      try {
        if (!selectedItems.value.length) {
          toast.add({
            severity: 'warn',
            summary: 'Предупреждение',
            detail: 'Выберите позиции для изменения статуса',
            life: 3000
          })
          return
        }

        const orderItemPairs = selectedItems.value.map(item => [
          orderDetails.value.order_id,
          item.item_id
        ])

        const response = await apiService.put('/rewards/batch-update-status', {
          order_item_pairs: orderItemPairs,
          status: 'Не выплачено'
        })

        if (response.data.success) {
          // Обновляем статусы в диалоге
          selectedItems.value.forEach(item => {
            item.payment_status = 'Не выплачено'
          })
          
          selectedItems.value = []
          await loadRewardsData()
          
          toast.add({
            severity: 'success',
            summary: 'Успешно',
            detail: `Обновлено ${response.data.updated_count} позиций`,
            life: 3000
          })
        }
      } catch (error) {
        console.error('Ошибка массового обновления:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось обновить статусы позиций',
          life: 5000
        })
      }
    }

    // Массовые операции для заказов (устаревшие, но оставлены)
    const markAsPaid = async () => {
      toast.add({
        severity: 'info',
        summary: 'Информация',
        detail: 'Откройте детали заказа для управления статусами отдельных позиций',
        life: 3000
      })
    }

    const markAsUnpaid = async () => {
      toast.add({
        severity: 'info',
        summary: 'Информация',
        detail: 'Откройте детали заказа для управления статусами отдельных позиций',
        life: 3000
      })
    }

    // Экспорт в Excel
    const exportToExcel = async () => {
      try {
        exportLoading.value = true
        
        const response = await apiService.post('/rewards/export', {}, {
          responseType: 'blob'
        })
        
        // Создаем ссылку для скачивания файла
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `rewards_export_${new Date().toISOString().split('T')[0]}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: 'Файл экспорта загружен',
          life: 3000
        })
      } catch (error) {
        console.error('Ошибка экспорта:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось выполнить экспорт',
          life: 5000
        })
      } finally {
        exportLoading.value = false
      }
    }

    // Экспорт деталей конкретного заказа
    const exportOrderDetails = async () => {
      try {
        if (!orderDetails.value) return
        
        const response = await apiService.post(`/rewards/${orderDetails.value.order_id}/export`, {}, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `order_${orderDetails.value.order_id}_rewards_details.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        
        toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: 'Детали заказа экспортированы',
          life: 3000
        })
      } catch (error) {
        console.error('Ошибка экспорта деталей заказа:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось экспортировать детали заказа',
          life: 5000
        })
      }
    }

    // Вспомогательные методы
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB'
      }).format(amount || 0)
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('ru-RU')
    }

    const getStatusSeverity = (status) => {
      return status === 'Закрыт' ? 'success' : 'warning'
    }

    const getRowClass = (data) => {
      return data.unpaid_rewards > 0 ? 'unpaid-row' : ''
    }

    // Загрузка данных при монтировании
    onMounted(() => {
      loadRewardsData()
    })

    return {
      // Данные
      rewardsData,
      filteredRewards,
      loading,
      exportLoading,
      summaryLoaded,
      selectedOrders,
      invoicesDialogVisible,
      invoicesLoading,
      invoices,
      invoicePagination,
      invoiceFirst,
      selectedInvoice,
      orderDetailsVisible,
      orderDetailsLoading,
      orderDetails,
      selectedOrderId,
      selectedItems,
      invoiceDetail,
      invoiceDetailLoading,
      filters,
      statusOptions,
      totalUnpaidRewards,
      invoiceTotalRecords,
      
      // Методы
      loadRewardsData,
      applyFilters,
      debouncedFilter,
      clearFilters,
      openInvoicesModal,
      loadInvoices,
      showOrderDetails,
      togglePaymentStatus,
      toggleItemPaymentStatus,
      markAsPaid,
      markAsUnpaid,
      markSelectedItemsAsPaid,
      markSelectedItemsAsUnpaid,
      exportToExcel,
      exportOrderDetails,
      loadInvoiceDetail,
      onInvoicesPage,
      formatCurrency,
      formatDate,
      getStatusSeverity,
      getRowClass
    }
  }
}
</script>

<style scoped>
.rewards-page {
  padding: 1rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-header h1 {
  margin: 0;
  color: #495057;
}

.header-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.rewards-badge {
  background: #e3f2fd;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border-left: 4px solid #2196f3;
}

.rewards-badge .label {
  font-weight: 500;
  color: #666;
  margin-right: 0.5rem;
}

.rewards-badge .value {
  font-weight: 600;
  color: #2196f3;
  font-size: 1.1em;
}

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  align-items: end;
}

.p-field {
  display: flex;
  flex-direction: column;
  min-width: 180px;
}

.filter-actions {
  flex-direction: row;
  align-items: flex-end;
  gap: 0.75rem;
}

.p-field label {
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
}

.table-section {
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-buttons {
  display: flex;
  gap: 0.5rem;
}

.customer-cell {
  display: flex;
  flex-direction: column;
}

.customer-name {
  font-weight: 500;
}

.reward-amount {
  font-weight: 600;
  color: #4caf50;
}

.reward-total {
  font-weight: 600;
  color: #4caf50;
  font-size: 1.2em;
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
}

.order-details {
  padding: 1rem 0;
}

.order-info {
  margin-bottom: 2rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-item label {
  font-weight: 500;
  color: #666;
  margin-bottom: 0.25rem;
}

.info-item span {
  font-weight: 600;
}

.items-section {
  margin-top: 2rem;
}

.items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.items-actions {
  display: flex;
  gap: 0.5rem;
}

.status-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-toggle-btn {
  min-width: 2rem !important;
  height: 2rem !important;
}

 .invoices-dialog {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 2fr);
  gap: 1.5rem;
  min-height: 400px;
}

.invoices-table-section,
.invoice-detail-section {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.invoices-table-section {
  max-width: 340px;
}

.invoice-detail-section {
  flex: 1;
}

.invoices-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.invoice-detail h3 {
  margin-top: 0;
  color: #495057;
}

.invoice-detail h4 {
  margin-top: 1.5rem;
  color: #495057;
}

.invoice-items-table {
  overflow: auto;
}

.invoice-detail-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  min-height: 200px;
  text-align: center;
}

.loading-container {
  text-align: center;
  padding: 2rem;
}

.loading-container p {
  margin-top: 1rem;
  color: #666;
}

/* Выделение строк с невыплаченными вознаграждениями */
:deep(.unpaid-row) {
  background-color: #fff3e0 !important;
}

:deep(.unpaid-row:hover) {
  background-color: #ffe0b2 !important;
}

/* Адаптивность */
@media (max-width: 768px) {
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .invoices-dialog {
    grid-template-columns: 1fr;
  }
}
</style>